import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Button
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout } from 'components/atoms'
import { InputBuild, SelectBuild } from 'components/molecules'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { formFilterFiles } from './form'
import { FilterFilesFormValues, FilterFilesProps } from './interfaces'
import { schema } from './schema'
import { FormFilterFilesElements } from './types'

const initialState: FilterFilesFormValues = {
    ufContrato: 'PR',
    cnpjAgenteFinanceiro: '',
    numeroContrato: '',
    numeroAditivo: '',
    chassi: ''
}

export function FilterFiles({
    onSearch,
    isLoading,
    isBackoffice
}: FilterFilesProps) {
    const formData = useMemo(() => formFilterFiles, [])

    const {
        register,
        formState: { errors },
        watch,
        handleSubmit,
        reset,
        setValue
    } = useForm<FilterFilesFormValues>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: initialState
    })

    const ufContrato = watch('ufContrato')

    useEffect(() => {
        if (ufContrato !== 'ES') {
            setValue('chassi', '')
            setValue('numeroAditivo', '')
        }
    }, [ufContrato, setValue])

    const onSubmit = (value: FilterFilesFormValues) => {
        onSearch({
            ...value,
            ufContrato: value.ufContrato?.trim().toUpperCase(),
            cnpjAgenteFinanceiro: value.cnpjAgenteFinanceiro.replace(/\D/g, ''),
            numeroContrato: value.numeroContrato?.trim(),
            numeroAditivo: value.numeroAditivo?.trim(),
            chassi: value.chassi?.trim()
        })
    }

    const handleClear = () => {
        reset(initialState)
    }

    const shouldShowField = (id?: string) => {
        if (!id) return true

        if (['chassi', 'numeroAditivo'].includes(id)) {
            return ufContrato === 'ES'
        }

        return true
    }

    return (
        <Accordion
            allowMultiple
            backgroundColor="white"
            marginBottom={4}
            defaultIndex={[0]}
        >
            <AccordionItem>
                <h2>
                    <AccordionButton
                        backgroundColor="white"
                        className="white-hover"
                    >
                        <Box
                            as="h2"
                            flex="1"
                            textAlign="left"
                            fontSize={20}
                            fontWeight={700}
                        >
                            Filtrar arquivo(s):
                        </Box>
                        <AccordionIcon />
                    </AccordionButton>
                </h2>

                <AccordionPanel pb={4} backgroundColor="white">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Box backgroundColor="white">
                            <Layout type="row" gutter={{ y: 1.5 }}>
                                {formData.map(({ layout, input, select }, key) => {
                                    const id = select?.id || input?.id

                                    if (!shouldShowField(id)) {
                                        return null
                                    }

                                    return (
                                        <Layout {...layout} key={key}>
                                            {select ? (
                                                <SelectBuild
                                                    {...select}
                                                    err={
                                                        errors[
                                                            select.id as FormFilterFilesElements
                                                        ]
                                                    }
                                                    {...register(
                                                        select.id as FormFilterFilesElements
                                                    )}
                                                />
                                            ) : (
                                                input && (
                                                    <InputBuild
                                                        {...input}
                                                        err={
                                                            errors[
                                                                input.id as FormFilterFilesElements
                                                            ]
                                                        }
                                                        {...register(
                                                            input.id as FormFilterFilesElements
                                                        )}
                                                        value={watch(
                                                            input.id as FormFilterFilesElements
                                                        )}
                                                    />
                                                )
                                            )}
                                        </Layout>
                                    )
                                })}
                            </Layout>

                            <Box
                                paddingY={8}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={6}
                            >
                                <Button
                                    colorScheme="primary"
                                    variant="outline"
                                    onClick={handleClear}
                                >
                                    Limpar Campos
                                </Button>

                                <Button
                                    colorScheme="primary"
                                    type="submit"
                                    isLoading={isLoading}
                                    isDisabled={isBackoffice}
                                >
                                    Filtrar
                                </Button>
                            </Box>
                        </Box>
                    </form>
                </AccordionPanel>
            </AccordionItem>
        </Accordion>
    )
}